import React, { useEffect, useRef, useState } from "react";
import { RobotIcon } from "../Auth/onboarding/icons";
import { statusStyles, avatarColors } from "./data";
import { StarIcon, TagIcon, KebabIcon, CollapseIcon, SmileyEmojiIcon, PaperclipIcon, SendIcon, CheckCircleIcon } from "./icons";
import ActivityTab from "./ActivityTab";
import DetailsTab from "./DetailsTab";

const subTabs = ["Conversation", "Details", "Activity"];

/**
 * The socket lives on the page, not here: an owner can hold several
 * conversations at once, so join state has to outlive whichever one is on
 * screen. This pane renders the live thread for the selected conversation and
 * drives join / leave / send through the callbacks it is given.
 */
export default function ConversationDetail({
  conversation,
  assistantId,
  autoJoin = false,
  live = false,
  joining = false,
  connected = false,
  liveMessages = null,
  liveError = "",
  onJoin,
  onLeave,
  onSend,
  onEndConversation,
  loading = false,
}) {
  const [activeSubTab, setActiveSubTab] = useState("Conversation");
  const [favorited, setFavorited] = useState(false);
  const [reply, setReply] = useState("");
  const [localMessages, setLocalMessages] = useState(conversation?.messages || []);

  const threadRef = useRef(null);
  // Which conversation the auto-join has already fired for, so leaving is not
  // immediately undone by the effect re-joining.
  const autoJoinedRef = useRef("");

  const conversationId = conversation?.id;
  const messages = conversation?.messages;

  // Switching conversations resets the pane. `messages` is a dependency too:
  // the list row renders first with just a preview, then the full transcript
  // arrives from the detail endpoint under the same id — keying only on the id
  // would leave the thread showing the stale, shorter version.
  useEffect(() => {
    setLocalMessages(messages || []);
  }, [conversationId, messages]);

  useEffect(() => {
    setReply("");
    setActiveSubTab("Conversation");
  }, [conversationId]);

  // Pinned to the newest message: a reply arriving below the fold makes the
  // pane look unchanged, which is exactly the bug this is meant to fix.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  });

  // Arrives from ?join=1 on the mail's link: the AI asked for a human, so
  // taking over on arrival is the point of the link. It waits for the socket
  // and fires once per conversation.
  useEffect(() => {
    if (!autoJoin || !connected || live || joining) return;
    if (!conversationId || autoJoinedRef.current === conversationId) return;

    autoJoinedRef.current = conversationId;
    onJoin?.(conversationId);
  }, [autoJoin, connected, live, joining, conversationId, onJoin]);

  // While live the server is the source of truth for the thread: its join ack
  // replaces the REST transcript, and every message after it arrives by event.
  const thread = liveMessages || localMessages;

  const handleSend = () => {
    const text = reply.trim();
    if (!text || !live) return;

    onSend?.(conversationId, text);
    setReply("");
  };

  if (!conversation) {
    return (
      <section className="dash-card conv-detail-card conv-detail-empty">
        <p>Select a conversation to view the transcript.</p>
      </section>
    );
  }

  // A status the API sends that isn't one of the five known labels has no
  // palette entry, so this must not be indexed blind.
  const style = statusStyles[conversation.status] || {};
  const lead = conversation.lead || {};
  const metaLine = [lead.email, lead.company, lead.jobTitle].filter(Boolean);

  return (
    <section className="dash-card conv-detail-card">
      <div className="conv-detail-header">
        <div className="conv-detail-heading">
          <h2>{conversation.name}</h2>
          {conversation.online && <span className="conv-online-dot conv-online-dot--inline" />}
          {conversation.status && (
            <span className="conv-status-badge" style={{ background: style.bg, color: style.color }}>
              {conversation.status}
            </span>
          )}
          {live && (
            <span className="conv-live-badge">
              <span className="conv-live-dot" />
              You&apos;re live
            </span>
          )}
        </div>

        <div className="conv-detail-actions">
          {live ? (
            <button
              type="button"
              className="onboarding__btn onboarding__btn--secondary conv-join-btn"
              onClick={() => onLeave?.(conversationId)}
            >
              Leave chat
            </button>
          ) : (
            <button
              type="button"
              className="onboarding__btn onboarding__btn--primary conv-join-btn"
              onClick={() => onJoin?.(conversationId)}
              disabled={joining || !connected}
              title={connected ? "" : "Connecting to the live channel…"}
            >
              {(joining || !connected) && (
                <span className="install-status-spinner" aria-hidden="true" />
              )}
              {joining ? "Joining..." : connected ? "Join chat" : "Connecting..."}
            </button>
          )}
        </div>

        {/* <div className="conv-detail-actions">
          <button
            type="button"
            className={`conv-icon-btn${favorited ? " is-active" : ""}`}
            onClick={() => setFavorited((v) => !v)}
            aria-label="Favorite"
          >
            <StarIcon filled={favorited} />
          </button>
          <button type="button" className="conv-icon-btn" aria-label="Tag conversation">
            <TagIcon />
          </button>
          <button type="button" className="conv-icon-btn" aria-label="More options">
            <KebabIcon />
          </button>
          <button type="button" className="conv-icon-btn" aria-label="Collapse">
            <CollapseIcon />
          </button>
        </div> */}
      </div>

      {metaLine.length > 0 && (
        <p className="conv-detail-meta">{metaLine.join(" • ")}</p>
      )}
      {(lead.location || lead.website) && (
        <p className="conv-detail-meta">
          {lead.location}
          {lead.location && lead.website && " • "}
          {lead.website && (
            <a href={`https://${lead.website}`} target="_blank" rel="noreferrer">
              {lead.website}
            </a>
          )}
        </p>
      )}

      {liveError && <p className="auth-error">{liveError}</p>}

      {live && (
        <p className="conv-live-note">
          You&apos;ve taken over this conversation - the assistant won&apos;t
          reply until you leave.
        </p>
      )}

      <div className="conv-subtabs">
        {subTabs.map((tab) => (
          <button
            type="button"
            key={tab}
            className={`conv-subtab${activeSubTab === tab ? " is-active" : ""}`}
            onClick={() => setActiveSubTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeSubTab === "Conversation" ? (
        <>
          <div className="conv-thread" ref={threadRef}>
            {loading && (
              // The list row stands in while the transcript loads, so without
              // this the pane looks finished but empty.
              <div className="conv-thread-loading">
                {[0, 1, 2].map((row) => (
                  <div className="conv-message conv-message--skeleton" key={row}>
                    <span className="conv-skeleton conv-skeleton--avatar" />
                    <div className="conv-message-body">
                      <span className="conv-skeleton conv-skeleton--label" />
                      <span className="conv-skeleton conv-skeleton--bubble" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && (
              <div className="conv-date-divider">
                <span>{conversation.date}</span>
              </div>
            )}

            {!loading && thread.map((message, i) => {
              // Driven by the message's own role — comparing the sender name
              // to a hardcoded "Maya" meant every real assistant reply was
              // rendered as if the visitor had sent it.
              const isAssistant = message.role === "assistant";
              const isAgent = message.role === "agent";

              // Notes the inbox writes itself (joined, replaced, resumed).
              if (message.role === "system") {
                return (
                  <p className="conv-system-note" key={i}>
                    {message.text}
                  </p>
                );
              }

              return (
                <div
                  className={`conv-message${isAssistant ? " is-assistant" : ""}${isAgent ? " is-agent" : ""}`}
                  key={i}
                >
                  <span
                    className="conv-message-avatar"
                    style={!isAssistant ? { background: isAgent ? "var(--hostor-ink)" : avatarColors[conversation.color] } : undefined}
                  >
                    {isAssistant ? <RobotIcon /> : isAgent ? "You" : conversation.initials}
                  </span>
                  <div className="conv-message-body">
                    <span className="conv-message-label">
                      {message.sender} <span>{message.time}</span>
                    </span>
                    <p className="conv-message-bubble">{message.text}</p>

                    {message.plan && (
                      <div className="conv-plan-card">
                        <h4>{message.plan.name}</h4>
                        <p className="conv-plan-price">
                          <strong>{message.plan.price}</strong> / {message.plan.period}
                        </p>
                        <ul>
                          {message.plan.features.map((feature) => (
                            <li key={feature}>
                              <CheckCircleIcon />
                              {feature}
                            </li>
                          ))}
                        </ul>
                        <button type="button" className="onboarding__btn onboarding__btn--primary conv-plan-btn">
                          View all features
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {conversation.event && (
              <div className="conv-event">
                <CheckCircleIcon />
                <span>
                  <strong>{conversation.event.text}</strong> <span>{conversation.event.time}</span>
                  <br />
                  {conversation.event.detail}
                </span>
              </div>
            )}
          </div>

          {conversation.ended ? (
            <div className="conv-ended-banner">This conversation has ended.</div>
          ) : (
            <div className="conv-reply-row">
              <input
                type="text"
                placeholder={
                  live
                    ? "Reply to the visitor..."
                    : "Join the chat to reply to this visitor"
                }
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                disabled={!live}
              />
              <button type="button" className="conv-reply-icon" aria-label="Insert emoji">
                <SmileyEmojiIcon />
              </button>
              <button type="button" className="conv-reply-icon" aria-label="Attach file">
                <PaperclipIcon />
              </button>
              <button
                type="button"
                className="conv-reply-send"
                aria-label="Send reply"
                onClick={handleSend}
                disabled={!live || !reply.trim()}
              >
                <SendIcon />
              </button>
            </div>
          )}

          {/* <div className="conv-footer-row">
            <a href="#history">Chat history</a>
            {!conversation.ended && (
              <button type="button" className="conv-end-btn" onClick={onEndConversation}>
                End conversation
              </button>
            )}
          </div> */}
        </>
      ) : activeSubTab === "Details" ? (
        <DetailsTab conversation={conversation} />
      ) : activeSubTab === "Activity" ? (
        <ActivityTab assistantId={assistantId} conversationId={conversationId} />
      ) : (
        <div className="onboarding__card--placeholder conv-subtab-placeholder">
          <h3>{activeSubTab} is coming soon</h3>
          <p>We&apos;re still building this tab.</p>
        </div>
      )}
    </section>
  );
}
